import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@mui/joy';
import { Sparkle as SparkleIcon } from '@phosphor-icons/react';
import api from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { WebsitePageCreateResponse, WebsitePageSummary } from '../../../types/websiteBuilder';

interface AddPageDialogProps {
  open: boolean;
  projectId: number | string;
  /** Existing pages — used to disable the Reviews preset once it exists and to flag slug clashes early. */
  pages: WebsitePageSummary[];
  onClose: () => void;
  onCreated: (result: WebsitePageCreateResponse) => void;
}

type PageKind = 'custom' | 'reviews';

/** Mirrors the backend slug rule: lowercase words joined by hyphens. */
const slugFromTitle = (title: string): string =>
  title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const AddPageDialog: React.FC<AddPageDialogProps> = ({ open, projectId, pages, onClose, onCreated }) => {
  const [kind, setKind] = useState<PageKind>('custom');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [brief, setBrief] = useState('');
  const [showInHeader, setShowInHeader] = useState(true);
  const [showInFooter, setShowInFooter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasReviewsPage = pages.some((p) => p.slug === 'reviews');

  // Reset the form each time the dialog opens (and only then — a parent
  // refetch while typing must not wipe the brief)
  useEffect(() => {
    if (!open) return;
    setKind('custom');
    setTitle('');
    setSlug('');
    setSlugTouched(false);
    setBrief('');
    setShowInHeader(true);
    setShowInFooter(false);
    setSubmitting(false);
    setError(null);
  }, [open]);

  // Keep the slug in step with the title until the user edits it by hand
  useEffect(() => {
    if (!slugTouched) setSlug(slugFromTitle(title));
  }, [title, slugTouched]);

  // Server is the single validator; these only give instant feedback
  const slugClash = kind === 'custom' && slug.length > 0 && pages.some((p) => p.slug === slug);
  const slugInvalid = kind === 'custom' && slug.length > 0 && !SLUG_PATTERN.test(slug);
  const canSubmit =
    !submitting
    && (kind === 'reviews' || (title.trim().length > 0 && !slugClash && !slugInvalid && brief.trim().length >= 10));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.websiteBuilder.createPage(
        projectId,
        kind === 'reviews'
          ? { page_type: 'reviews', show_in_header: showInHeader, show_in_footer: showInFooter }
          : {
            page_type: 'custom',
            title: title.trim(),
            slug,
            brief: brief.trim(),
            show_in_header: showInHeader,
            show_in_footer: showInFooter,
          },
      );
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not add the page. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <ModalDialog sx={{ width: { xs: '95vw', sm: 560 }, maxWidth: '95vw' }}>
        <ModalClose disabled={submitting} />
        <DialogTitle>Add a page</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>What kind of page?</FormLabel>
              <RadioGroup
                value={kind}
                onChange={(e) => setKind(e.target.value as PageKind)}
                sx={{ gap: 1 }}
              >
                <Radio
                  value="custom"
                  label="Custom page — describe it and the AI writes it"
                />
                <Radio
                  value="reviews"
                  disabled={hasReviewsPage}
                  label={hasReviewsPage ? 'Customer Reviews page (already added)' : 'Customer Reviews page — live review grid, trust copy, and a big call to action'}
                />
              </RadioGroup>
            </FormControl>

            {kind === 'custom' && (
              <>
                <FormControl required>
                  <FormLabel>Page title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Roof Financing"
                    slotProps={{ input: { maxLength: 120 } }}
                  />
                </FormControl>

                <FormControl error={slugInvalid || slugClash}>
                  <FormLabel>Page address</FormLabel>
                  <Input
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value.toLowerCase());
                    }}
                    startDecorator={<Typography level="body-sm" sx={{ color: 'text.tertiary' }}>/</Typography>}
                    endDecorator={<Typography level="body-sm" sx={{ color: 'text.tertiary' }}>/</Typography>}
                    placeholder="roof-financing"
                  />
                  <FormHelperText>
                    {slugClash
                      ? 'A page already exists at this address.'
                      : slugInvalid
                        ? 'Use lowercase letters, numbers and hyphens only.'
                        : 'Lowercase letters, numbers and hyphens. Becomes part of the page URL.'}
                  </FormHelperText>
                </FormControl>

                <FormControl required>
                  <FormLabel>What should this page say?</FormLabel>
                  <Textarea
                    minRows={4}
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="Explain what the page is for, who it's aimed at, and anything specific to include — e.g. financing partners, terms, how to apply, and a note that approval takes minutes."
                    slotProps={{ textarea: { maxLength: 4000 } }}
                  />
                  <FormHelperText>
                    The AI writes the page from this brief using your business details. You can revise it afterwards in the Edits tab.
                  </FormHelperText>
                </FormControl>
              </>
            )}

            {kind === 'reviews' && (
              <Alert variant="soft" color="neutral">
                Adds <strong>/reviews/</strong> with trust-building copy written for your company, your live
                reviews widget, and a large call to action. The widget stays hidden until you have reviews to show.
              </Alert>
            )}

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Navigation</Typography>
              <Stack spacing={1}>
                <Switch
                  checked={showInHeader}
                  onChange={(e) => setShowInHeader(e.target.checked)}
                  endDecorator="Show in the header menu"
                  sx={{ alignSelf: 'flex-start' }}
                />
                <Switch
                  checked={showInFooter}
                  onChange={(e) => setShowInFooter(e.target.checked)}
                  endDecorator="Show in the footer links"
                  sx={{ alignSelf: 'flex-start' }}
                />
              </Stack>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                Links are added to every page once the new page is written. Publish the site afterwards to push them live.
              </Typography>
            </Box>

            {error && <Alert color="danger" variant="soft">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            startDecorator={<SparkleIcon size={16} />}
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {kind === 'reviews' ? 'Add Reviews page' : 'Write this page'}
          </Button>
          <Button variant="plain" color="neutral" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default AddPageDialog;
